import { useEffect, useMemo, useState } from "react";
import { Car } from "lucide-react";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { getVehicleTelemetry } from "@/services/vehicleTelemetry";

const METRICS = [
  ["speed_kph", "Geschwindigkeit", "km/h"],
  ["rpm", "Drehzahl", "rpm"],
  ["fuel_percent", "Kraftstoff", "%"],
  ["battery_voltage", "Bordspannung", "V"],
  ["coolant_celsius", "Kühlmittel", "°C"],
  ["odometer_km", "Kilometerstand", "km"],
  ["range_km", "Reichweite", "km"],
  ["engine_load_percent", "Motorlast", "%"],
  ["dtc_count", "Diagnosecodes", ""],
  ["charging_state", "Ladestatus", ""],
  ["ignition_state", "Zündstatus", ""],
];

const isVehicleDevice = (device) => String(device?.metadata?.category || "").toLowerCase() === "vehicle_obd2";

const formatMetric = (value, unit) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") {
    const rendered = Number.isInteger(value) ? String(value) : value.toFixed(1);
    return unit ? `${rendered} ${unit}` : rendered;
  }
  return String(value).replaceAll("_", " ").toUpperCase();
};

export const VehicleTelemetryPanel = ({ devices, token }) => {
  const vehicleDevices = useMemo(
    () => (devices?.rooms || []).flatMap((room) => room.devices || []).filter(isVehicleDevice),
    [devices],
  );
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!vehicleDevices.length) {
      setSnapshots([]);
      setError("");
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    setError("");
    Promise.allSettled(vehicleDevices.map((device) => getVehicleTelemetry(device.id, token)))
      .then((results) => {
        if (cancelled) return;
        const available = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
        const failed = results.filter((result) => result.status === "rejected");
        setSnapshots(available);
        setError(failed.length && !available.length ? failed[0].reason?.message || "Fahrzeugtelemetrie konnte nicht geladen werden." : "");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, vehicleDevices]);

  if (!vehicleDevices.length) {
    return (
      <AionEmptyState
        icon={Car}
        title="Noch kein OBD2-Fahrzeug verbunden."
        description="AION zeigt hier nur echte, tenant-gebundene OBD2-Telemetrie. Es werden keine Demo-Fahrzeugdaten erzeugt."
        testId="vehicle-telemetry-empty"
      />
    );
  }

  return (
    <div className="aion-view aion-view-stack" data-testid="vehicle-telemetry-panel">
      <section className="aion-vehicle-hero" data-testid="aion-vehicle-hero">
        <div className="aion-vehicle-hero-copy">
          <span>AION DRIVE</span>
          <h2>Fahrzeugstatus</h2>
          <p>{vehicleDevices.length} verbundenes Fahrzeug{vehicleDevices.length === 1 ? "" : "e"} · Read-only Telemetrie</p>
        </div>
        <div className="aion-vehicle-hero-icon" aria-hidden="true"><Car size={58} /></div>
      </section>
      <AionSectionCard
        title="Fahrzeug · OBD2"
        eyebrow="READ-ONLY TELEMETRIE"
        subtitle="Echte Fahrzeugwerte aus dem vorhandenen Geräte-/Hardwarepfad. Keine Fernsteuerung."
        testId="vehicle-telemetry-intro"
      >
        <div className="aion-note" data-testid="vehicle-telemetry-safety-note">
          Nur Anzeige: keine Schlüssel-, Tür-, Start-/Stop-, Lenk-, Fahr- oder sonstigen Fahrzeugaktionen. AION registriert hierfür keine ausführbare CAR-Action.
        </div>
      </AionSectionCard>

      {loading && !snapshots.length ? <div className="aion-note" data-testid="vehicle-telemetry-loading">Telemetrie wird geladen …</div> : null}
      {error ? <div className="aion-error-box" data-testid="vehicle-telemetry-error">{error}</div> : null}

      {snapshots.map((snapshot) => {
        const telemetry = snapshot.telemetry || {};
        const visibleMetrics = METRICS.filter(([key]) => Object.prototype.hasOwnProperty.call(telemetry, key));
        return (
          <AionSectionCard
            key={snapshot.device_id}
            title={snapshot.device_name || "Fahrzeug"}
            eyebrow={`${String(snapshot.connection_state || "unknown").toUpperCase()} · ${String(snapshot.verification_state || "UNVERIFIED").toUpperCase()}`}
            subtitle={[snapshot.manufacturer, snapshot.model, snapshot.protocol?.toUpperCase()].filter(Boolean).join(" · ") || "OBD2 Telemetriequelle"}
            testId={`vehicle-telemetry-card-${snapshot.device_id}`}
          >
            {visibleMetrics.length ? (
              <div className="aion-compact-status-grid" data-testid={`vehicle-telemetry-grid-${snapshot.device_id}`}>
                {visibleMetrics.map(([key, label, unit]) => (
                  <div className="aion-compact-status-box" key={key} data-testid={`vehicle-telemetry-${key}-${snapshot.device_id}`}>
                    <span>{label}</span>
                    <strong>{formatMetric(telemetry[key], unit)}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className="aion-note" data-testid={`vehicle-telemetry-no-values-${snapshot.device_id}`}>
                Das Fahrzeug ist verbunden, aber es liegen aktuell keine validierten OBD2-Werte vor.
              </div>
            )}
          </AionSectionCard>
        );
      })}
    </div>
  );
};
